import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { agentsService } from '../../services/agents.service';
import { countriesService } from '../../services/countries.service';
import { statesService } from '../../services/states.service';
import { citiesService } from '../../services/cities.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import useAvailabilityCheck from '../../helper/useAvailabilityCheck';
import { formAvailabilityMsg, formField, formInputStyle, formLabelStyle, formReadOnlyStyle, formSectionCard, formSectionTitle, formSelectStyles } from "../common/FormUI";

const GRAD = "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const TITLE_OPTIONS = ["Mr", "Mrs", "Ms", "Dr"].map(v => ({ value: v, label: v }));
const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const initialAgent = {
  password: "",
  company_name: "",
  registered_as: "",
  title: "",
  first_name: "",
  last_name: "",
  gender: "",
  executive: "",
  sales_person: "",
  remarks: "",
  t_and_c_agreed: false,
  mobile: "",
  email: "",
  address: "",
  pincode: "",
  city_id: "",
  state_id: "",
  country_id: "",
  status: "active",
};

const AddAgent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const editAgentId = location.state?.agentId;
  const isEdit = !!editAgentId;

  const [loading, setLoading] = useState(false);
  const [agent, setAgent] = useState(initialAgent);
  const availability = useAvailabilityCheck(agentsService);
  // values of the record being edited; they are not "taken" by themselves
  const originalRef = useRef({});
  // option values are ids (the agent API stores country_id / state_id / city_id)
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  useEffect(() => {
    (async () => {
      const countries = await loadCountryOptions();
      if (isEdit) loadAgentDetail();
    })();
  }, [editAgentId]);

  const toOptions = (list) => (list || []).map(i => ({ value: i.id, label: i.name }));

  const loadCountryOptions = async () => {
    try {
      const res = await countriesService.list({ page: 1, status: "active" });
      const opts = toOptions(res?.resData?.data);
      setCountryOptions(opts);
      return opts;
    } catch (err) {
      setCountryOptions([]);
      return [];
    }
  }

  const loadStateOptions = async (countryId) => {
    if (!countryId) { setStateOptions([]); return []; }
    try {
      const res = await statesService.list({ page: 1, status: "active", country_id: countryId });
      const opts = toOptions(res?.resData?.data);
      setStateOptions(opts);
      return opts;
    } catch (err) {
      setStateOptions([]);
      return [];
    }
  }

  const loadCityOptions = async (stateId) => {
    if (!stateId) { setCityOptions([]); return []; }
    try {
      const res = await citiesService.list({ page: 1, status: "active", state_id: stateId });
      const opts = toOptions(res?.resData?.data);
      setCityOptions(opts);
      return opts;
    } catch (err) {
      setCityOptions([]);
      return [];
    }
  }

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const loadAgentDetail = async () => {
    try {
      setLoading(true);
      const res = await agentsService.details(editAgentId);
      if (res?.success) {
        const v = res.resData;
        originalRef.current = { mobile: v?.mobile || "", email: (v?.email || "").toLowerCase() };
        setAgent({
          password: "",
          company_name: v?.company_name || "",
          registered_as: v?.registered_as || "",
          title: v?.title || "",
          first_name: v?.first_name || "",
          last_name: v?.last_name || "",
          gender: v?.gender || "",
          executive: v?.executive || "",
          sales_person: v?.sales_person || "",
          remarks: v?.remarks || "",
          t_and_c_agreed: !!v?.t_and_c_agreed,
          mobile: v?.mobile || "",
          email: v?.email || "",
          address: v?.address || "",
          pincode: v?.pincode || "",
          city_id: v?.city_id || "",
          state_id: v?.state_id || "",
          country_id: v?.country_id || "",
          status: v?.status || "active",
        });
        await loadStateOptions(v?.country_id);
        await loadCityOptions(v?.state_id);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "email") {
      const email = value.trim().toLowerCase();
      availability.check("email", "email", email, { message: "Email already exists", valid: /^\S+@\S+\.\S+$/.test(email), skip: email === originalRef.current.email });
    }
    setAgent({ ...agent, [name]: value });
  }

  const handleMobileChange = (e) => {
    const mobile = e.target.value.replace(/\D/g, "").slice(0, 10);
    availability.check("mobile", "phone", mobile, { message: "Mobile already exists", valid: /^\d{10}$/.test(mobile), skip: mobile === originalRef.current.mobile });
    setAgent({ ...agent, mobile });
  }

  const handlePincodeChange = (e) => {
    setAgent({ ...agent, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) });
  }

  const handleCountryChange = (o) => {
    setAgent({ ...agent, country_id: o ? o.value : "", state_id: "", city_id: "" });
    setCityOptions([]);
    loadStateOptions(o?.value);
  }

  const handleStateChange = (o) => {
    setAgent({ ...agent, state_id: o ? o.value : "", city_id: "" });
    loadCityOptions(o?.value);
  }

  const validate = () => {
    if (!agent.company_name.trim()) return "Company name is required";
    if (!agent.executive.trim()) return "Executive name is required";
    if ((!isEdit || agent.password) && agent.password.length < 6) return "Password must be at least 6 characters";
    if (!/^\d{10}$/.test(agent.mobile)) return "Enter a valid 10 digit mobile number";
    if (!/^\S+@\S+\.\S+$/.test(agent.email)) return "Enter a valid email address";
    if (availability.taken.mobile || availability.taken.email) return availability.taken.mobile || availability.taken.email;
    if (availability.isChecking) return "Please wait while availability is being checked";
    if (!agent.address.trim()) return "Address is required";
    if (!/^\d{6}$/.test(agent.pincode)) return "Enter a valid 6 digit pincode";
    if (!agent.city_id || !agent.state_id || !agent.country_id) return "Country, state and city are required";
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: isEdit ? "Update agent?" : "Save agent?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Yes, update" : "Yes, save",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      const { password, ...rest } = agent;
      const body = { ...rest, country_id: Number(rest.country_id), state_id: Number(rest.state_id), city_id: Number(rest.city_id) };
      const res = isEdit
        ? await agentsService.update(editAgentId, { ...body, ...(password ? { password } : {}) })
        : await agentsService.create({ ...body, password });
      if (res?.success) {
        await withReactContent(Swal).fire({ title: "Success", text: res.message, icon: "success" });
        if (isEdit) {
          navigate("/agency/list");
        } else {
          setAgent(initialAgent);
          setStateOptions([]);
          setCityOptions([]);
        }
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (<ContentLoader loaderType="circleSpin" />)

  const labelStyle = formLabelStyle;
  const inputStyle = formInputStyle;
  const readOnlyStyle = formReadOnlyStyle;
  const selectStyles = formSelectStyles;
  const sectionCard = formSectionCard;
  const sectionTitle = formSectionTitle;
  const field = (label, required, node, cls = "col-12 col-md-6 col-lg-4") => formField(label, required, node, cls);
  const title = isEdit ? "Update Agent" : "Add Agent";

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>{isEdit ? "Update Agent" : "Add New Agent"}</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/setting" style={{ color: "#475569" }}>Setting</Link>   
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>           
              <Link to="/agency/list" style={{ color: "#475569" }}>Agency List</Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>{title}</span>
            </nav>
          </div>
          <Link to="/agency/list" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-list-ul"></i> Agency List
          </Link>
        </div>

        <form className="v2a-form" onSubmit={handleSubmit}>

<div style={sectionCard}>
<div className="row g-3">
{field("Company Name", true,
                  <input type="text" className="form-control" placeholder="e.g. Sky Travels Pvt Ltd" name="company_name"
                    value={agent.company_name} onChange={handleChange} style={inputStyle} />
                )}
</div>
</div>

<div style={sectionCard}>
              {sectionTitle("bi-person-lines-fill", "Contact Detail", "Primary contact person for this agency")}


              <div className="row g-3">
                {field("Title", false,
                  <Select
                    options={TITLE_OPTIONS}
                    value={TITLE_OPTIONS.find(o => o.value === agent.title) || null}
                    onChange={(o) => setAgent({ ...agent, title: o ? o.value : "", gender: { Mr: "male", Mrs: "female", Ms: "female" }[o?.value] || agent.gender })}
                    placeholder="Select title"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("First Name", false,
                  <input type="text" className="form-control" placeholder="First name" name="first_name"
                    value={agent.first_name} onChange={handleChange} style={inputStyle} />
                )}
                {field("Last Name", false,
                  <input type="text" className="form-control" placeholder="Last name" name="last_name"
                    value={agent.last_name} onChange={handleChange} style={inputStyle} />
                )}
                {field("Gender", false,
                  <Select
                    options={GENDER_OPTIONS}
                    value={GENDER_OPTIONS.find(o => o.value === agent.gender) || null}
                    onChange={(o) => setAgent({ ...agent, gender: o ? o.value : "" })}
                    placeholder="Select gender"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Executive", true,
                  <input type="text" className="form-control" placeholder="Contact person name" name="executive"
                    value={agent.executive} onChange={handleChange} style={inputStyle} />
                )}
                {field("Mobile", true,
                  <><input type="text" inputMode="numeric" className="form-control" placeholder="10 digit mobile number" name="mobile"
                    value={agent.mobile} onChange={handleMobileChange} style={inputStyle} />
                  {formAvailabilityMsg(availability.taken.mobile, availability.checking.mobile)}</>
                )}
                {field("Email", true,
                  <><input type="email" className="form-control" placeholder="name@company.com" name="email"
                    value={agent.email} onChange={handleChange} style={inputStyle} />
                  {formAvailabilityMsg(availability.taken.email, availability.checking.email)}</>
                )}
                {field(isEdit ? "New Password" : "Password", !isEdit,
                  <input type="password" autoComplete="new-password" className="form-control" placeholder={isEdit ? "Leave blank to keep current" : "Minimum 6 characters"} name="password"
                    value={agent.password} onChange={handleChange} style={inputStyle} />
                )}
              </div>
            </div>
            
            {/* <div style={sectionCard}>
              {sectionTitle("bi-building", "Company Detail", "Registered business information")}
              <div className="row g-3">
                {field("Company Name", true,
                  <input type="text" className="form-control" placeholder="e.g. Sky Travels Pvt Ltd" name="company_name"
                    value={agent.company_name} onChange={handleChange} style={inputStyle} />
                )}
                
                {field("Registered As", false,
                  <input type="text" className="form-control" placeholder="e.g. Proprietorship" name="registered_as"
                    value={agent.registered_as} onChange={handleChange} style={inputStyle} />
                )}
                {field("Sales Person", false,
                  <input type="text" className="form-control" placeholder="Sales person name" name="sales_person"
                    value={agent.sales_person} onChange={handleChange} style={inputStyle} />
                )}
                {field("Remarks", false,
                  <input type="text" className="form-control" placeholder="Any remarks" name="remarks"
                    value={agent.remarks} onChange={handleChange} style={inputStyle} />
                )}
                <div className="col-12">
                  <label className="d-flex align-items-center gap-2" style={{ fontSize: 13, color: "#4b5563" }}>
                    <input type="checkbox" className="form-check-input m-0" checked={agent.t_and_c_agreed}
                      onChange={(e) => setAgent({ ...agent, t_and_c_agreed: e.target.checked })} />
                    Terms and conditions agreed
                  </label>
                </div>
              </div>
            </div>             */}

            <div style={sectionCard}>
              {sectionTitle("bi-geo-alt", "Address Detail", "Registered office address")}
              <div className="row g-3">
                {field("Address", true,
                  <input type="text" className="form-control" placeholder="Street, area, landmark" name="address"
                    value={agent.address} onChange={handleChange} style={inputStyle} />,
                  "col-12"
                )}
                {field("Country", true,
                  <Select
                    options={countryOptions}
                    value={countryOptions.find(o => o.value === agent.country_id) || null}
                    onChange={handleCountryChange}
                    placeholder="Select country"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("State", true,
                  <Select
                    options={stateOptions}
                    value={stateOptions.find(o => o.value === agent.state_id) || null}
                    onChange={handleStateChange}
                    placeholder="Select state"
                    isClearable
                    isDisabled={!agent.country_id}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("City", true,
                  <Select
                    options={cityOptions}
                    value={cityOptions.find(o => o.value === agent.city_id) || null}
                    onChange={(o) => setAgent({ ...agent, city_id: o ? o.value : "" })}
                    placeholder="Select city"
                    isClearable
                    isDisabled={!agent.state_id}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Pincode", true,
                  <input type="text" inputMode="numeric" className="form-control" placeholder="6 digit pincode" name="pincode"
                    value={agent.pincode} onChange={handlePincodeChange} style={inputStyle} />
                )}
                {field("Status", false,
                  <Select
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find(o => o.value === agent.status) || null}
                    onChange={(o) => setAgent({ ...agent, status: o ? o.value : "active" })}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <Link to="/agency/list" className="btn px-4 d-flex align-items-center"
                style={{ background: "#fff", border: "1px solid #c3cad6", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}>
                Back
              </Link>
              <button type="submit" className="btn px-4 text-white fw-semibold"
                style={{ background: GRAD, border: "none", borderRadius: 8, height: 40, fontSize: 13 }}>
                {title}
              </button>
            </div>

        </form>
      </section>
    </>
  );
}

export default AddAgent;
